"use strict";

var _interopRequireDefault = require("@babel/runtime/helpers/interopRequireDefault");
Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.default = void 0;
var _extends2 = _interopRequireDefault(require("@babel/runtime/helpers/extends"));
var _objectWithoutProperties2 = _interopRequireDefault(require("@babel/runtime/helpers/objectWithoutProperties"));
var _react = _interopRequireWildcard(require("react"));
var _propTypes = _interopRequireDefault(require("prop-types"));
var _checkmark = _interopRequireDefault(require("@ux/icon/checkmark"));
var _minus = _interopRequireDefault(require("@ux/icon/minus"));
var _text = _interopRequireDefault(require("@ux/text"));
var _useControllableState = _interopRequireDefault(require("@ux/component-utilities/lib/use-controllable-state"));
const _excluded = ["checked", "defaultChecked", "indeterminate", "onChange", "invalid"],
  _excluded2 = ["as", "helpMessage", "invalid", "label", "errorMessage"];
/* eslint-disable complexity */
function _getRequireWildcardCache(e) { if ("function" != typeof WeakMap) return null; var r = new WeakMap(), t = new WeakMap(); return (_getRequireWildcardCache = function (e) { return e ? t : r; })(e); }
function _interopRequireWildcard(e, r) { if (!r && e && e.__esModule) return e; if (null === e || "object" != typeof e && "function" != typeof e) return { default: e }; var t = _getRequireWildcardCache(r); if (t && t.has(e)) return t.get(e); var n = { __proto__: null }, a = Object.defineProperty && Object.getOwnPropertyDescriptor; for (var u in e) if ("default" !== u && Object.prototype.hasOwnProperty.call(e, u)) { var i = a ? Object.getOwnPropertyDescriptor(e, u) : null; i && (i.get || i.set) ? Object.defineProperty(n, u, i) : n[u] = e[u]; } return n.default = e, t && t.set(e, n), n; }
const UX_CHECKBOX_CLASSNAME = 'ux-checkbox';
const asOptions = ['option'];

/**
 * Creates a native HTML input element
 *
 * @param {Object} props - Component properties
 * @param {Boolean} [props.checked] - Determines if the input should be checked (controlled)
 * @param {String} [props.defaultChecked] - Sets the initial checked state of the input (uncontrolled)
 * @param {Boolean} [props.disabled] - Determines if the input should be disabled
 * @param {String} [props.errorMessage] - Message that shows up under label as critical
 * @param {String} [props.helpMessage] - Message that shows up under label as information
 * @param {String} props.id - Sets an `id` on the input
 * @param {Boolean} [props.indeterminate] - Sets indeterminate on the input
 * @param {Element} [props.inputRef] - Sets the `ref` for the input element
 * @param {String} props.name - Sets the name of the input
 * @param {Function} [props.onBlur] - Callback for when the component loses focus
 * @param {Function} [props.onChange] - Callback when the value changes
 * @param {Boolean} [props.readOnly] - Determines if the input should be readonly
 * @param {Number} [props.tabIndex] - Sets the tabIndex
 * @param {Node} [props.value] - Sets the value of the input
 * @returns {Component} - HTML Input Element
 */
const Input = /*#__PURE__*/_react.default.forwardRef(function Input(props, ref) {
  const _ref = props || {},
    {
      checked: controllableProp,
      defaultChecked: defaultProp = false,
      indeterminate,
      onChange: onControllableChange = Function.prototype,
      invalid
    } = _ref,
    rest = (0, _objectWithoutProperties2.default)(_ref, _excluded);
  const [checked, onChange] = (0, _useControllableState.default)({
    prop: controllableProp,
    defaultProp,
    onChange: onControllableChange
  });

  // style is explicitly omitted, do not override.
  delete rest.style;
  const inputRef = (0, _react.useRef)({});
  (0, _react.useImperativeHandle)(ref, () => inputRef.current, [inputRef]);

  // Indeterminate cannot be set as a prop, must set with DOM node
  (0, _react.useEffect)(() => {
    if (inputRef.current) {
      inputRef.current.indeterminate = indeterminate;
    }
  }, [indeterminate]);
  const _onChange = (0, _react.useCallback)(ev => {
    onChange(ev.target.checked, {
      event: ev
    });
  }, [onChange]);
  return /*#__PURE__*/_react.default.createElement("input", (0, _extends2.default)({
    checked: checked,
    className: `${UX_CHECKBOX_CLASSNAME}-input`,
    onChange: _onChange,
    ref: inputRef,
    "aria-invalid": invalid,
    type: "checkbox"
  }, rest));
});
Input.propTypes = {
  /** Determines if the input should be checked */
  checked: _propTypes.default.bool,
  /** Sets the initial checked state of the input */
  defaultChecked: _propTypes.default.bool,
  /** Determines if the input should be disabled */
  disabled: _propTypes.default.bool,
  /** Sets an `id` on the input */
  id: _propTypes.default.string.isRequired,
  /** Sets indeterminate on the input */
  indeterminate: _propTypes.default.bool,
  /** Sets the `ref` for the input element */
  inputRef: _propTypes.default.any,
  /** Sets the name of the input */
  name: _propTypes.default.string.isRequired,
  /** Callback for when the component loses focus */
  onBlur: _propTypes.default.func,
  /** Callback when the value changes */
  onChange: _propTypes.default.func,
  /** Determines if the input should be readonly */
  readOnly: _propTypes.default.bool,
  /** Allows a tabIndex to be set */
  tabIndex: _propTypes.default.oneOf([0, -1]),
  /** Sets the value of the input */
  value: _propTypes.default.node
};

/**
 * Creates a checkbox component
 *
 * @param {Object} props - Component properties
 * @param {String} [props.as] - Display component in different way
 * @param {Boolean} [props.checked] - Determines if the input should be checked (controlled)
 * @param {String} [props.defaultChecked] - Sets the initial checked state of the input (uncontrolled)
 * @param {Boolean} [props.disabled] - Determines if the input should be disabled
 * @param {String} props.id - Sets an `id` on the input
 * @param {Boolean} [props.indeterminate] - Sets indeterminate on the input
 * @param {Boolean} [props.invalid] - Determines if the component should be invalid
 * @param {Node} [props.label] - Provides the text describing the input's value
 * @param {String} props.name - Sets the name of the input
 * @param {Function} [props.onChange] - Callback when the value changes
 * @param {Boolean} [props.readOnly] - Determines if the input should be readonly
 * @param {Node} [props.value] - Sets the value of the input
 * @returns {Component} - Checkbox component
 */
const Checkbox = /*#__PURE__*/_react.default.forwardRef(function Checkbox(_ref2, ref) {
  let {
      as,
      helpMessage,
      invalid,
      label,
      errorMessage
    } = _ref2,
    inputProps = (0, _objectWithoutProperties2.default)(_ref2, _excluded2);
  const {
    id,
    disabled,
    readOnly
  } = inputProps || {};
  const helpMessageId = helpMessage && `${id}-help`;
  const errorMessageId = errorMessage && `${id}-error`;
  return /*#__PURE__*/_react.default.createElement(_text.default.div, {
    "data-version": "2400.6",
    className: `${UX_CHECKBOX_CLASSNAME}-shell`,
    as: "input",
    emphasis: invalid ? 'critical' : null,
    "aria-disabled": disabled,
    "aria-readonly": readOnly,
    role: as === 'option' ? 'option' : null
  }, /*#__PURE__*/_react.default.createElement("span", {
    className: `${UX_CHECKBOX_CLASSNAME}-lineheightbox`,
    "data-lineheight": label && 'I'
  }, /*#__PURE__*/_react.default.createElement(Input, (0, _extends2.default)({}, inputProps, {
    invalid: invalid,
    ref: ref,
    "aria-describedby": helpMessageId,
    "aria-errormessage": errorMessageId
  })), /*#__PURE__*/_react.default.createElement("span", {
    className: `${UX_CHECKBOX_CLASSNAME}-indicator`
  }, /*#__PURE__*/_react.default.createElement(_minus.default, {
    className: `${UX_CHECKBOX_CLASSNAME}-indeterminate`
  }), /*#__PURE__*/_react.default.createElement(_checkmark.default, {
    className: `${UX_CHECKBOX_CLASSNAME}-checkmark`
  }))), label && /*#__PURE__*/_react.default.createElement("label", {
    htmlFor: id,
    className: `${UX_CHECKBOX_CLASSNAME}-label`
  }, label), errorMessage && /*#__PURE__*/_react.default.createElement(_text.default.p, {
    text: errorMessage,
    as: "caption",
    id: errorMessageId,
    emphasis: "critical",
    role: "alert"
  }), !errorMessage && helpMessage && /*#__PURE__*/_react.default.createElement(_text.default.p, {
    text: helpMessage,
    as: "caption",
    id: helpMessageId,
    emphasis: "passive"
  }));
});
Checkbox.propTypes = {
  /** Determines if the input should be checked */
  checked: _propTypes.default.bool,
  /** Sets the initial checked state of the input */
  defaultChecked: _propTypes.default.bool,
  /** Determines if the input should be disabled */
  disabled: _propTypes.default.bool,
  /** Sets an `id` on the input */
  id: _propTypes.default.string.isRequired,
  /** Sets indeterminate on the input */
  indeterminate: _propTypes.default.bool,
  /** Sets the `ref` for the input element */
  inputRef: _propTypes.default.object,
  /** Sets the name of the input */
  name: _propTypes.default.string.isRequired,
  /** Callback when the value changes */
  onChange: _propTypes.default.func,
  /** Determines if the input should be readonly */
  readOnly: _propTypes.default.bool,
  /** Allows a tabIndex to be set */
  tabIndex: _propTypes.default.oneOf([0, -1]),
  /** Sets the value of the input */
  value: _propTypes.default.node,
  /** Configure the ARIA role for component */
  as: _propTypes.default.oneOf(asOptions),
  /** Optional error message which appears below the input */
  errorMessage: _propTypes.default.string,
  /** Optional message which appears below the input */
  helpMessage: _propTypes.default.string,
  /** Determines if the component should be invalid */
  invalid: _propTypes.default.bool,
  /** Provides the text describing the input's value */
  label: _propTypes.default.oneOfType([_propTypes.default.string, _propTypes.default.element])
};
Checkbox.displayName = 'Checkbox';
var _default = exports.default = Checkbox;